/**
 * @file dashboard-attention.ts
 * @description Маршруты и сортировка строк «Требует внимания» (phase19.24
 * этап 1, шаг 3; выделено из `wms-dashboard-texts.ts` по ревью 2026-09-20).
 *
 * Строки приходят с сервера с видом цели (`href.type`), а маршрут админки
 * знает только клиент — отсюда реестр `ATTENTION_ROUTE`: новая цель = запись,
 * а не ветка в разметке секции.
 */

import { wmsHref } from "./dashboard-href";
import type {
  WmsAttentionHrefDto,
  WmsAttentionItemDto,
  WmsAttentionKind,
  WmsAttentionTargetType,
} from "../../../lib/api-wms-dashboard";

/**
 * Ключ отбора остатков от сервера (`href.type = "stock_filter"`) → параметры
 * журнала остатков. «Без этикеток» открывается с уже отмеченными партиями —
 * кладовщик сразу жмёт «Печать».
 */
const STOCK_FILTER_PARAMS: Record<string, Record<string, string>> = {
  unlabeled: { labels: "none", select: "all" },
};

/**
 * Вид цели строки «Требует внимания» → маршрут админки.
 *
 * `sku` ведёт в остатки по одной фасовке (`?skuId=`) и обязательно с
 * `positive=0`: повод строки — «остаток ниже минимума», а нулевой остаток
 * отбор по умолчанию прячет (TD-58), и кладовщик открывал пустой реестр.
 * `lot` — реестр истекающих партий: карточки партии в админке нет, а нужен
 * список, где эта партия видна вместе с соседями по сроку.
 */
export const ATTENTION_ROUTE: Record<
  WmsAttentionTargetType,
  (basePath: string, id: string, warehouseId?: string) => string
> = {
  picking: (base, id) => `${base}/picking/${id}`,
  receipt: (base, id) => `${base}/receipts/${id}`,
  transfer: (base, id) => `${base}/transfers/${id}`,
  writeoff: (base, id) => `${base}/writeoffs/${id}`,
  sku: (base, id) => wmsHref(base, "/stock", { skuId: id, positive: "0" }),
  lot: (base) => `${base}/expiring`,
  // У `bins` сервер кладёт в `id` СКЛАД, а не ячейку (см. `wms-dashboard.dto.ts`).
  bins: (base, id) => wmsHref(base, "/bins", { wh: id }),
  // phase19.22: `id` — ключ отбора остатков, склад — из фильтра дашборда.
  stock_filter: (base, id, warehouseId) => {
    const filter = STOCK_FILTER_PARAMS[id];
    return filter ? wmsHref(base, "/stock", { ...filter, wh: warehouseId }) : `${base}/stock`;
  },
};

/**
 * Поводы со своим маршрутом — сильнее вида цели.
 *
 * «Партии без места» без приёмки сервер адресует складом (`bins`), но чинится
 * такая строка не в справочнике ячеек, а в остатках: отбор «Без места» с уже
 * отмеченными партиями и кнопкой «Разместить выбранные» (решение пользователя
 * 2026-09-20). Со ссылкой на приёмку (`receipt`) ничего не меняем — там
 * оформляется лист размещения по документу.
 */
const ATTENTION_KIND_ROUTE: Partial<
  Record<WmsAttentionKind, (basePath: string, href: WmsAttentionHrefDto) => string | null>
> = {
  unplaced: (base, href) =>
    href.type === "bins"
      ? wmsHref(base, "/stock", { unplaced: "1", select: "all", wh: href.id })
      : null,
};

/**
 * Маршрут строки внимания; незнакомый вид цели ведёт на главную склада.
 *
 * `kind` необязателен: старые вызовы знают только цель, а особый маршрут есть
 * лишь у пары поводов.
 */
export function attentionHref(
  basePath: string,
  href: WmsAttentionHrefDto,
  kind?: WmsAttentionKind,
  /** Склад, выбранный на сводке (для отборов остатков). */
  warehouseId?: string,
): string {
  const byKind = kind ? ATTENTION_KIND_ROUTE[kind]?.(basePath, href) : null;
  if (byKind) return byKind;
  // Реестр закрыт по типу, но сервер может прислать вид новее клиента —
  // тогда ссылка не должна стать `undefined` в адресной строке.
  const route = ATTENTION_ROUTE[href.type] as
    | ((basePath: string, id: string, warehouseId?: string) => string)
    | undefined;
  return route ? route(basePath, href.id, warehouseId) : basePath;
}

/** Вес тона при сортировке: «работа стоит» — выше «успеть до». */
const SEVERITY_ORDER: Record<WmsAttentionItemDto["severity"], number> = {
  danger: 0,
  warning: 1,
};

/** Строки внимания сверху вниз: сначала danger, порядок сервера сохраняем. */
export function sortAttention(items: WmsAttentionItemDto[]): WmsAttentionItemDto[] {
  return [...items].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );
}

/** Ключ строки внимания: своего `id` у неё нет, собираем из цели и повода. */
export function attentionKey(item: WmsAttentionItemDto): string {
  return `${item.kind}:${item.href.type}:${item.href.id}`;
}
